import { useId, useState } from 'react';
import type { ReactNode } from 'react';

import './Disclosure.css';

export type DisclosureVariant = 'row' | 'inline';

interface DisclosureProps {
  /**
   * The label on the control. A string in the ordinary case; a fragment where
   * the row carries its own layout, as the check catalogue's rows do.
   */
  summary: ReactNode;
  /** Secondary text on the control row, after the label. Never the only cue. */
  hint?: ReactNode;
  children: ReactNode;
  /** Open on first render. Uncontrolled: the component owns the state after. */
  defaultOpen?: boolean;
  /**
   * Controlled open state. Pass with onOpenChange when a parent has to know,
   * as the workspace does when it expands the selected finding.
   */
  open?: boolean;
  onOpenChange?(open: boolean): void;
  /**
   * 'row' is a full-width control with a rule; 'inline' sits inside a
   * sentence. Both are the same button, the same ARIA and the same motion.
   */
  variant?: DisclosureVariant;
  className?: string;
  /** Applied to the control, for callers that own its internal layout. */
  headClassName?: string;
  /** Applied to the panel's inner element, for callers that own its padding. */
  panelClassName?: string;
}

/**
 * The one expand/collapse in the application.
 *
 * The control is a real button, so Enter and Space operate it and it takes
 * focus in document order without any key handling of our own. It carries
 * aria-expanded, and aria-controls pointing at a panel that is always in the
 * DOM — the panel is `hidden` when closed rather than unmounted, so the id
 * aria-controls names always resolves and nothing inside a closed panel can
 * take focus. The children are only rendered while open, so a closed
 * disclosure costs no render.
 *
 * Motion lives in Disclosure.css and is expressed entirely in the motion
 * tokens, which collapse to 0ms under prefers-reduced-motion.
 */
export default function Disclosure({
  summary,
  hint,
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  variant = 'row',
  className,
  headClassName,
  panelClassName,
}: DisclosureProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const panelId = useId();

  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : uncontrolledOpen;

  const toggle = () => {
    const next = !open;
    if (!controlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const classes = [
    'disc',
    `disc--${variant}`,
    open ? 'disc--open' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <button
        type="button"
        className={['disc__head', headClassName].filter(Boolean).join(' ')}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
      >
        <span className="disc__summary">{summary}</span>
        {hint ? <span className="disc__hint">{hint}</span> : null}
        {/* The mark repeats what aria-expanded says, for the eye. */}
        <span className="disc__mark" aria-hidden="true">
          {open ? '–' : '+'}
        </span>
      </button>

      <div className="disc__panel" id={panelId} hidden={!open}>
        <div className={['disc__body', panelClassName].filter(Boolean).join(' ')}>
          {open ? children : null}
        </div>
      </div>
    </div>
  );
}
